import numeral from 'numeral';

// French typography uses a no-break space as thousands separator and a comma as
// decimal separator, with the currency symbol after the amount. U+00A0 rather
// than the typographically nicer U+202F: the monospace face used in the price
// grid has no glyph for U+202F and collapses it to almost nothing.
// The numbers themselves always come from `@placekit/pricing`; this only
// reformats numeral's output, deterministically, so server and client render the
// same string (no `Intl` locale data involved).
const NBSP = '\u00a0'; // U+00A0 NO-BREAK SPACE

/**
 * Integer with locale-aware thousands separators.
 * @arg {number} value Number to format.
 * @arg {string} [locale='en'] Locale code.
 * @return {string}
 */
export const formatCount = (value, locale = 'en') => {
  const output = numeral(value).format('0,0');
  return locale === 'fr' ? output.replace(/,/g, NBSP) : output;
};

/**
 * Compact integer ("10k", "1m"), locale-aware ("10 k", "1 M").
 * @arg {number} value Number to format.
 * @arg {string} [locale='en'] Locale code.
 * @return {string}
 */
export const formatCompact = (value, locale = 'en') => {
  const output = numeral(value).format('0a');
  return locale === 'fr'
    ? output.replace(/([kmb])$/, (_, unit) => NBSP + (unit === 'k' ? 'k' : unit.toUpperCase()))
    : output;
};

/**
 * US dollar amount, locale-aware.
 * @arg {number} value Amount in USD.
 * @arg {string} [locale='en'] Locale code.
 * @arg {string} [format='$0,0'] numeral format, must start with `$`.
 * @return {string}
 */
export const formatUSD = (value, locale = 'en', format = '$0,0') => {
  const output = numeral(value).format(format);
  if (locale !== 'fr') {
    return output;
  }
  return `${output.replace('$', '').replace(/,/g, NBSP).replace('.', ',')}${NBSP}$`;
};

/**
 * Price of a single request, locale-aware ("$0.0030", "0,0030 $").
 * @arg {number} price Per-request price in USD, from `@placekit/pricing`.
 * @arg {string} [locale='en'] Locale code.
 * @return {string}
 */
export const formatPerRequest = (price, locale = 'en') => formatUSD(price, locale, '$0.0000');

/**
 * Price of 1,000 requests, locale-aware ("$3.00", "3,00 $").
 * @arg {number} price Per-request price in USD, from `@placekit/pricing`.
 * @arg {string} [locale='en'] Locale code.
 * @return {string}
 */
export const formatPer1k = (price, locale = 'en') => formatUSD(price * 1000, locale, '$0.00');
